import React, { useState, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { useFamilyFeatures } from '@/hooks/useFamilyFeatures'
import { useNavigate } from 'react-router-dom'
import { apiClient } from '@/utils/api'
import { getTodayDateString, extractDateInTimezoneSync } from '@/utils/timezoneUtils'
import {
  ClipboardList, Gift, TrendingUp, Star, Zap,
  Calendar, CheckCircle, Clock, UtensilsCrossed,
  MessageSquare, GraduationCap, Bus, AlertCircle, ArrowRightLeft
} from 'lucide-react'

interface KidStats {
  todayTasks: number
  completedToday: number
  totalPoints: number
  lifetimePoints: number
  availableRewards: number
  upcomingEvents: number
  pendingRequests: number
  nextSchoolEvent: string
  tonightsDinner: string
}

export const KidDashboardDesktop: React.FC = () => {
  const { user, updateUser } = useAuth()
  const { isFeatureEnabled } = useFamilyFeatures()
  const navigate = useNavigate()

  const [stats, setStats] = useState<KidStats>({
    todayTasks: 0,
    completedToday: 0,
    totalPoints: user?.available_points ?? user?.total_points ?? 0,
    lifetimePoints: 0,
    availableRewards: 0,
    upcomingEvents: 0,
    pendingRequests: 0,
    nextSchoolEvent: '',
    tonightsDinner: ''
  })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchStats()
  }, [])

  const fetchStats = async () => {
    try {
      setLoading(true)
      const todayStr = await getTodayDateString()

      // Fetch fresh user data along with other stats to ensure we have latest points
      const [currentUser, assignmentsRes, rewardsRes, eventsRes, kidRequestsRes, mealsRes] = await Promise.all([
        apiClient.getCurrentUser().catch(() => null),
        apiClient.get('/api/assignments'),
        apiClient.getRewards().catch(() => []),
        apiClient.get('/api/events').catch(() => []),
        apiClient.get('/api/events/kid-requests').catch(() => []),
        apiClient.get(`/api/meals/week-plan?start=${todayStr}&end=${todayStr}`).catch(() => [])
      ])

      // Use fresh user data from API, fall back to cached user
      const freshUser = currentUser || user

      // Update auth state with fresh user data so header/menu show correct values
      if (currentUser) {
        updateUser(currentUser)
      }

      // Process assignments - API returns assigned_to field
      const assignments = assignmentsRes?.assignments || assignmentsRes?.data || []
      const myAssignments = assignments.filter((a: any) => a.assigned_to === freshUser?.id)

      const todayTasks = myAssignments.filter((a: any) => {
        const dueDate = a.due_date ? extractDateInTimezoneSync(a.due_date) : null
        return dueDate === todayStr && a.status !== 'completed' && a.status !== 'verified'
      }).length

      const completedToday = myAssignments.filter((a: any) => {
        return a.status === 'completed' && a.completed_at?.startsWith(todayStr)
      }).length

      // Process rewards
      const rewards = Array.isArray(rewardsRes) ? rewardsRes : []
      const availableRewards = rewards.filter((r: any) =>
        r.active && (freshUser?.available_points ?? freshUser?.total_points ?? 0) >= r.cost_points
      ).length

      // Process events
      const events = Array.isArray(eventsRes) ? eventsRes : (eventsRes?.data || [])
      const upcomingEvents = events.filter((e: any) => e.event_date >= todayStr).length

      // Process kid requests - count pending requests for this user
      const kidRequests = Array.isArray(kidRequestsRes) ? kidRequestsRes : (kidRequestsRes?.data || kidRequestsRes?.requests || [])
      const pendingRequests = kidRequests.filter((r: any) =>
        r.requested_by === freshUser?.id && r.status === 'pending'
      ).length

      // Process meals - find tonight's dinner
      // API returns array of DayPlan objects with nested breakfast/lunch/dinner
      const mealPlans = Array.isArray(mealsRes) ? mealsRes : []
      const todayPlan = mealPlans.find((p: any) => p.date === todayStr)
      const tonightsDinner = todayPlan?.dinner?.meal_name || ''

      setStats({
        todayTasks,
        completedToday,
        totalPoints: freshUser?.available_points ?? freshUser?.total_points ?? 0,
        lifetimePoints: freshUser?.lifetime_points_earned || 0,
        availableRewards,
        upcomingEvents,
        pendingRequests,
        nextSchoolEvent: '',
        tonightsDinner
      })
    } catch (error) {
      console.error('Error fetching kid stats:', error)
    } finally {
      setLoading(false)
    }
  }

  const tiles = [
    {
      id: 'chores',
      title: 'My Chores',
      icon: ClipboardList,
      color: 'from-blue-500 to-indigo-600',
      stats: [
        { label: 'Due Today', value: stats.todayTasks, icon: Clock },
        { label: 'Completed', value: stats.completedToday, icon: CheckCircle }
      ],
      route: '/dashboard/chores'
    },
    {
      id: 'points',
      title: 'My Points',
      icon: Star,
      color: 'from-amber-500 to-orange-600',
      stats: [
        { label: 'Balance', value: stats.totalPoints, icon: Zap },
        { label: 'Lifetime', value: stats.lifetimePoints, icon: TrendingUp }
      ],
      route: '/dashboard/points'
    },
    {
      id: 'rewards',
      title: 'Rewards',
      icon: Gift,
      color: 'from-pink-500 to-rose-600',
      stats: [
        { label: 'Can Redeem', value: stats.availableRewards, icon: Gift }
      ],
      route: '/dashboard/rewards'
    },
    {
      id: 'trading',
      title: 'Trading',
      icon: ArrowRightLeft,
      color: 'from-indigo-500 to-purple-600',
      stats: [
        { label: 'Trade Chores', value: '', icon: ArrowRightLeft }
      ],
      route: '/dashboard/trading'
    },
    {
      id: 'calendar',
      title: 'Calendar',
      icon: Calendar,
      color: 'from-green-500 to-emerald-600',
      stats: [
        { label: 'Upcoming', value: stats.upcomingEvents, icon: Calendar }
      ],
      route: '/dashboard/calendar'
    },
    {
      id: 'meals',
      title: 'Meals',
      icon: UtensilsCrossed,
      color: 'from-orange-500 to-red-600',
      stats: [
        { label: 'Tonight', value: stats.tonightsDinner || 'No dinner planned', icon: UtensilsCrossed }
      ],
      route: '/dashboard/meals'
    },
    {
      id: 'requests',
      title: 'Requests',
      icon: MessageSquare,
      color: 'from-purple-500 to-violet-600',
      stats: [
        { label: 'Pending', value: stats.pendingRequests, icon: AlertCircle }
      ],
      route: '/dashboard/requests'
    },
    {
      id: 'school',
      title: 'School',
      icon: GraduationCap,
      color: 'from-cyan-500 to-blue-600',
      stats: [
        { label: 'Info & Events', value: '', icon: Bus }
      ],
      route: '/dashboard/school'
    }
  ]

  // Filter tiles based on feature flags
  const filteredTiles = tiles.filter(tile => {
    if (tile.id === 'school' && !isFeatureEnabled('school')) {
      return false
    }
    if (tile.id === 'trading' && !isFeatureEnabled('chore_bidding')) {
      return false
    }
    return true
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Tile Grid - 2x2 on mobile, 4 cols on desktop */}
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
        {filteredTiles.map((tile) => {
          const Icon = tile.icon
          return (
            <button
              key={tile.id}
              onClick={() => navigate(tile.route)}
              className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${tile.color} opacity-90 group-hover:opacity-100 transition-opacity`} />

              <div className="relative p-4 md:p-6 text-white">
                <div className="flex items-center justify-between mb-2 md:mb-4">
                  <Icon className="w-8 md:w-10 h-8 md:h-10" />
                  {typeof tile.stats[0]?.value === 'number' || (tile.stats[0]?.value !== '' && !isNaN(Number(tile.stats[0]?.value))) ? (
                    <div className="w-8 md:w-10 h-8 md:h-10 rounded-full bg-white/20 flex items-center justify-center">
                      <span className="text-base md:text-lg font-bold">
                        {tile.stats[0]?.value ?? 0}
                      </span>
                    </div>
                  ) : (
                    <div className="w-8 md:w-10 h-8 md:h-10 rounded-full bg-white/20 flex items-center justify-center">
                      <Icon className="w-4 md:w-5 h-4 md:h-5" />
                    </div>
                  )}
                </div>

                <h3 className="text-base md:text-xl font-bold mb-2 md:mb-3">{tile.title}</h3>

                <div className="space-y-1 md:space-y-2">
                  {tile.stats.map((stat, idx) => {
                    const StatIcon = stat.icon
                    return (
                      <div key={idx} className="flex items-center gap-1 md:gap-2 text-xs md:text-sm">
                        <StatIcon className="w-3 md:w-4 h-3 md:h-4 opacity-80" />
                        <span className="opacity-90">{stat.label}:</span>
                        <span className="font-semibold">{stat.value}</span>
                      </div>
                    )
                  })}
                </div>
              </div>
            </button>
          )
        })}
      </div>

      {/* Quick Stats Bar - Hidden on mobile, shown on desktop */}
      <div className="hidden md:block mt-8 bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-8">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">{stats.completedToday}</div>
              <div className="text-sm text-gray-500">Done Today</div>
            </div>
            <div className="h-12 w-px bg-gray-200" />
            <div className="text-center">
              <div className="text-3xl font-bold text-amber-600">{stats.totalPoints}</div>
              <div className="text-sm text-gray-500">Points Balance</div>
            </div>
            <div className="h-12 w-px bg-gray-200" />
            <div className="text-center">
              <div className="text-3xl font-bold text-pink-600">{stats.availableRewards}</div>
              <div className="text-sm text-gray-500">Rewards Available</div>
            </div>
          </div>

          <button
            onClick={() => navigate('/dashboard/chores')}
            className="px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-xl hover:shadow-lg transition-shadow"
          >
            Start Earning!
          </button>
        </div>
      </div>

      {/* Mobile CTA */}
      <div className="md:hidden mt-4 mb-20">
        <button
          onClick={() => navigate('/dashboard/chores')}
          className="w-full py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-xl shadow-lg"
        >
          Start Earning Points!
        </button>
      </div>
    </div>
  )
}
